import * as FileSaver from 'file-saver';
import JSZip from 'jszip';
import axios from './axios';
import * as filepath from './filepath';
import * as strings from './strings';

const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
};

export declare interface Source {
    name: string;
    url?: string;
    blob?: Blob;
    base64?: string;
}

export async function DownloadURLs(zipName: string, sources: Source[]) {
    const names = new Set();

    for (const s of sources) {
        const ext = filepath.Ext(s.name);
        const baseName = strings.TrimSuffix(s.name, ext);
        let counter = 2;
        while (names.has(s.name)) {
            s.name = `${baseName} (${counter})${ext}`;
            counter++;
        }
        names.add(s.name);
    }

    const zip = new JSZip();

    async function getSource(s: Source) {
        if (!s.blob && s.base64) {
            s.blob = b64toBlob(s.base64);
        }

        if (!s.blob && s.url) {
            const resp = await axios.get(s.url, {
                responseType: 'blob',
                withCredentials: false,
            });
            s.blob = resp.data as Blob;
        }

        if (!s.blob) {
            throw new Error('a zip source needs to result in a blob');
        }

        return {
            name: s.name,
            blob: s.blob,
        };
    }

    const p: Promise<{
        name: string;
        blob: Blob;
    }>[] = [];
    for (const s of sources) {
        p.push(getSource(s));
    }

    void Promise.all(p);

    for (const s of p) {
        const f = await s;
        zip.file(f.name, f.blob);
    }

    await zip.generateAsync({ type: 'blob' }).
        then(content => FileSaver.saveAs(content, zipName));
}
